/**
 * adsApprovals.ts — customer names, logos and quotes that ads may use only after the customer said yes in writing.
 *
 * A manager lists the name first ("waiting": still blocked everywhere). When the customer's written OK is in hand,
 * the manager records where it is and marks it "approved": from then on the ad rules let the name through.
 * Figures the customer agreed to (for example "40% faster") are allowed in copy too, but only while approved.
 */
import { z } from "zod";
import { AD_PRODUCT_IDS, PROOF_WORDS } from "./adsProducts.ts";
import { numbersIn } from "./adsChecks.ts";

/** "claim" is a word like "best" or "top" that ads may use because proof is on file (the name is the word itself). */
export const AD_APPROVAL_KINDS = ["name", "logo", "quote", "claim"] as const;
export const AD_APPROVAL_STATUSES = ["waiting", "approved"] as const;

const baseAdd = z.object({
  siteId: z.string().min(1).max(100),
  /** The name exactly as an ad would write it. */
  name: z.string().trim().min(3, "Enter the customer name.").max(120),
  kind: z.enum(AD_APPROVAL_KINDS).default("name"),
  /** Empty means every product. */
  product: z.enum(AD_PRODUCT_IDS).nullable().default(null),
  /** What the customer agreed to, in plain words. */
  covers: z.string().trim().max(300).default(""),
  /** Figures the customer agreed to be quoted on, such as "40% faster". */
  figures: z.array(z.string().trim().min(1).max(60)).max(10).default([]),
});
export const adNameApprovalAddSchema = baseAdd.refine((v) => v.kind !== "claim" || (PROOF_WORDS as readonly string[]).includes(v.name.toLowerCase()), {
  message: `A claim must be one of: ${PROOF_WORDS.join(", ")}.`,
  path: ["name"],
});
export type AdNameApprovalAdd = z.infer<typeof adNameApprovalAddSchema>;

export const adNameApprovalApproveSchema = z.object({
  /** Where the written OK lives (an email subject, a contract, a ticket). Required so there is always a record. */
  writtenOk: z.string().trim().min(5, "Say where the written OK is kept.").max(300),
});

export const adNameApprovalSchema = z.object({
  id: z.string(),
  siteId: z.string(),
  name: z.string(),
  kind: z.enum(AD_APPROVAL_KINDS),
  product: z.enum(AD_PRODUCT_IDS).nullable(),
  covers: z.string(),
  figures: z.array(z.string()),
  status: z.enum(AD_APPROVAL_STATUSES),
  writtenOk: z.string().nullable(),
  approvedBy: z.string().nullable(),
  approvedAt: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
});
export type AdNameApproval = z.infer<typeof adNameApprovalSchema>;

export const adNameApprovalListSchema = z.object({ items: z.array(adNameApprovalSchema) });

type Live = Pick<AdNameApproval, "name" | "status" | "product" | "figures"> & { kind?: AdNameApproval["kind"] };

/** What the ad checks need from the live (not retired) approvals for one product. */
export function approvalContext(rows: readonly Live[], product: string): { approvedNames: string[]; blockedNames: string[]; approvedFigures: string[] } {
  const mine = rows.filter((r) => (r.product === null || r.product === product) && r.kind !== "claim");
  const approved = mine.filter((r) => r.status === "approved");
  const lower = new Set(approved.map((r) => r.name.toLowerCase()));
  return {
    approvedNames: [...new Set(approved.map((r) => r.name))],
    // A name that is waiting stays blocked, unless another entry for the same name is already approved.
    blockedNames: [...new Set(mine.filter((r) => r.status === "waiting" && !lower.has(r.name.toLowerCase())).map((r) => r.name))],
    approvedFigures: approved.flatMap((r) => r.figures).flatMap((f) => numbersIn(f)),
  };
}

/** Words like "best" or "top" that the written proof on file allows for this product. */
export function provenClaimWords(rows: readonly Live[], product: string): string[] {
  return [...new Set(rows.filter((r) => r.kind === "claim" && r.status === "approved" && (r.product === null || r.product === product)).map((r) => r.name.toLowerCase()))];
}
